import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import AppLayout from "@/components/feature/AppLayout";
import SummaryCard from "@/components/feature/SummaryCard";
import ExpenseChart from "@/components/feature/ExpenseChart";
import CategoryChart, { type DonutDatum } from "@/components/feature/CategoryChart";
import { useToast } from "@/store/ToastContext";
import { computeCategoryTotals } from "@/services/api";
import { startOfWeek, startOfMonth, subtractMonths, monthLabel } from "@/utils/date";
import { formatCurrency } from "@/utils/currency";
import { paymentLabel } from "@/utils/format";
import type { Expense, Category, AppSettings, PeriodKey } from "@/models/types";
import { expenseApi, categoryApi, settingsApi } from "@/services/api";

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "week", label: "Esta semana" },
  { key: "month", label: "Este mes" },
  { key: "3months", label: "3 meses" },
  { key: "6months", label: "6 meses" },
  { key: "year", label: "Este año" },
  { key: "custom", label: "Personalizado" },
];

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function getRange(period: PeriodKey, from: string, to: string) {
  const now = new Date();
  let start: Date;
  let end: Date = now;
  switch (period) {
    case "week":
      start = startOfWeek(now);
      break;
    case "3months":
      start = subtractMonths(now, 3);
      break;
    case "6months":
      start = subtractMonths(now, 6);
      break;
    case "year":
      start = new Date(now.getFullYear(), 0, 1);
      break;
    case "custom":
      start = from ? new Date(from) : startOfMonth(now);
      end = to ? new Date(to) : now;
      break;
    default:
      start = startOfMonth(now);
  }
  return { start, end };
}

function monthBuckets(start: Date, end: Date, expenses: Expense[]) {
  const buckets: { label: string; total: number }[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`;
    const label = monthLabel(cursor);
    const total = expenses
      .filter((e) => e.date.slice(0, 7) === key)
      .reduce((s, e) => s + e.amount, 0);
    buckets.push({ label, total });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return buckets;
}

function dayBuckets(start: Date, end: Date, expenses: Expense[]) {
  const buckets: { label: string; total: number }[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(
      cursor.getDate(),
    )}`;
    const total = expenses
      .filter((e) => e.date === key)
      .reduce((s, e) => s + e.amount, 0);
    buckets.push({ label: String(cursor.getDate()), total });
    cursor.setDate(cursor.getDate() + 1);
  }
  return buckets;
}

export default function Statistics() {
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const [period, setPeriod] = useState<PeriodKey>("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        const [expensesData, categoriesData, settingsData] = await Promise.all([
          expenseApi.list(),
          categoryApi.list(),
          settingsApi.get(),
        ]);
        // Mapeo de campos snake_case a camelCase (por si el backend los devuelve así)
        const mappedExpenses = expensesData.map((e: any) => ({
          ...e,
          categoryId: e.category_id ?? e.categoryId,
          paymentMethod: e.payment_method ?? e.paymentMethod,
          createdAt: e.created_at ?? e.createdAt,
          receipt: e.receipt ?? null,
        }));
        setExpenses(mappedExpenses);
        setCategories(categoriesData);
        setSettings(settingsData);
      } catch (error) {
        console.error("Error cargando estadísticas:", error);
        showToast("Error al cargar los datos.", "error");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const currency = settings?.currency ?? "USD";

  const { start, end } = getRange(period, customFrom, customTo);

  const filtered = useMemo(
    () =>
      expenses.filter((e) => {
        const d = new Date(e.date);
        return d >= start && d <= end;
      }),
    [expenses, start, end],
  );

  const monthly = useMemo(
    () => monthBuckets(start, end, filtered),
    [filtered, start, end],
  );
  const daily = useMemo(
    () => dayBuckets(start, end, filtered),
    [filtered, start, end],
  );

  const totalPeriod = filtered.reduce((s, e) => s + e.amount, 0);
  const catTotals = computeCategoryTotals(filtered, categories).filter(
    (c) => c.total > 0,
  );
  const topCategory = catTotals[0];

  const monthsCount = monthly.length || 1;
  const monthlyAverage = totalPeriod / monthsCount;

  const paymentCounts = filtered.reduce<Record<string, number>>((acc, e) => {
    acc[e.paymentMethod] = (acc[e.paymentMethod] || 0) + 1;
    return acc;
  }, {});
  const topPayment = Object.entries(paymentCounts).sort(
    (a, b) => b[1] - a[1],
  )[0];

  const donutData: DonutDatum[] = catTotals.map((c) => ({
    name: c.category.name,
    value: c.total,
    color: c.category.color,
  }));

  const showDailyBar = period === "week" || period === "month";

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Selector de periodo */}
      <div className="flex items-center gap-1 overflow-x-auto rounded-full bg-background-100 border border-background-200 p-1 mb-6 w-full">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
              period === p.key
                ? "bg-primary-500 text-white"
                : "text-foreground-600 hover:bg-background-200"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === "custom" && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="rounded-md border border-secondary-300 bg-background-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <span className="text-foreground-500">—</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="rounded-md border border-secondary-300 bg-background-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      )}

      {/* Métricas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <SummaryCard
          title="Total del periodo"
          value={formatCurrency(totalPeriod, currency)}
          icon="ri-funds-line"
          tone="primary"
        />
        <SummaryCard
          title="Categoría con mayor gasto"
          value={topCategory?.category.name ?? "—"}
          icon="ri-price-tag-3-line"
          tone="accent"
          sub={
            topCategory
              ? formatCurrency(topCategory.total, currency)
              : undefined
          }
        />
        <SummaryCard
          title="Promedio mensual"
          value={formatCurrency(monthlyAverage, currency)}
          icon="ri-line-chart-line"
          tone="secondary"
        />
        <SummaryCard
          title="Método más utilizado"
          value={topPayment ? paymentLabel(topPayment[0] as any) : "—"}
          icon="ri-bank-card-line"
          tone="secondary"
          sub={topPayment ? `${topPayment[1]} usos` : undefined}
        />
      </div>

      {/* Tendencia mensual */}
      <div className="bg-background-50 border border-background-200 rounded-lg p-5 mb-4">
        <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
          Gastos por mes
        </h3>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthly} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="greenFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickFormatter={(v: number) => `$${v}`}
                width={50}
              />
              <Tooltip
                formatter={(value) => [formatCurrency(Number(value), currency), "Total"]}
                contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke="#22c55e"
                strokeWidth={2}
                fill="url(#greenFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Barras por día y dona */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
            {showDailyBar ? "Gastos por día" : "Gastos por mes"}
          </h3>
          <ExpenseChart
            data={showDailyBar ? daily : monthly}
            currencyCode={currency}
            height={240}
            color="#f59e0b"
          />
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-5">
          <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
            Gastos por categoría
          </h3>
          {donutData.length > 0 ? (
            <CategoryChart data={donutData} currencyCode={currency} />
          ) : (
            <p className="text-sm text-foreground-500 py-10 text-center">
              Sin datos en este periodo.
            </p>
          )}
        </div>
      </div>
    </AppLayout>
  );
}